import React from 'react';

import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions,
  Divider,
  styled,
  Typography,
  Stack,
  Box
} from '@mui/material';
import { CanLeadInvestmentsOptionNames } from 'activities/auth/OnboardingProfileActivity/dataService'; // TODO: directory path
import { InvestmentStageName } from 'generated/graphql';
import {
  fields,
  appEvents,
  enumCache
} from 'modules';
import {
  InvestmentStageMultiSelectField,
  InvestmentAreaMultiSelectField,
  InvestmentCategorySelectField,
  InvestmentGeographyGroupedMultiSelectField,
  USStateSelectField,
  useCharacterCount
} from 'modules';
import { ProfilePictureInputField } from 'modules';

import * as dataService from './dataService';



const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormData>();
const InvestmentStageMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormData, InvestmentStageName>(InvestmentStageMultiSelectField({
  label: 'Investment Stage'
}));
const InvestmentAreaMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormData, string>(InvestmentAreaMultiSelectField({
  label: 'Industry'
}));
const InvestmentCategorySelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentCategory>(InvestmentCategorySelectField({
  label: 'Category'
}));
const InvestmentGeographyGroupedMultiSelectFieldController = fields.genGroupedEnumMultiSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentGeography>(InvestmentGeographyGroupedMultiSelectField({
  label: 'Investment Geography (for investors only)'
}));
const CanLeadInvestmentsSelectFieldController = fields.genBasicSelectFieldControllerComponent<dataService.IFormData, CanLeadInvestmentsOptionNames>([['yes', 'Yes'], ['no', 'No']]);
const USStateSelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(USStateSelectField({
  label: 'State'
}));



const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  width: 1280,
  marginBottom: theme.spacing(8)
}));

const CenteredGrid = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
});

const Caption = styled(Typography)({
  opacity: 0.72,
  textAlign: 'center',
  wrap: 'nowrap',
  letterSpacing: 0.4,
  marginTop: 4,
  fontFamily: 'Roboto',
  color: '#486c82',
  fontSize: 12,
  width: 169
});

const RowGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const RoundPadBox = styled(Box)({
  width: 56,
  height: 56,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#C3D1D87F',
  borderRadius: 28
});

const MoneyIcon = styled(AttachMoneyIcon)({
  color: '#528898'
});

const HorizontalBar = styled(Box)({
  height: '2px',
  width: '23px',
  backgroundColor: '#C3D1D8',
  borderRadius: '1px'
});



interface IProps {
  siteUserId: string;
  companyProfileId: string;
  investorProfileId?: string;
}

export const FC: React.FC<IProps> = props => {
  const { data, form, loading, mutation } = dataService.useSaveUserProfile(props);
  const watch = form.watch('profilePictureFile');

  const chars = form.watch('companyDescription');
  const characterCount = useCharacterCount(chars);
  const isInvestor = !!props.investorProfileId;



  function handleClose() {
    appEvents.broadcast({
      type: 'modal',
      args: 'close'
    });
  }



  async function processSubmit(formData: dataService.IFormData) {
    try {
      await mutation.saveUserProfile(formData);

    } catch (error) {
      alert(error);

    } finally {
      handleClose();
    }
  }



  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="xl">
      <DialogTitle>Edit User</DialogTitle>

      <form onSubmit={form.handleSubmit(processSubmit)}>
        <DialogContent>
          <Grid
            container
            rowSpacing={3}
            spacing={6}>

            <CenteredGrid item xs={4}>
              <Stack justifyContent='center'>
                <ProfilePictureInputField.FC
                  value={watch ?? undefined}
                  initValue={data.profilePictureUrl}
                  onChange={fileName => {
                    form.setValue('profilePictureFile', fileName);
                  }} />

                <Caption>Your Company Logo</Caption>
              </Stack>
            </CenteredGrid>

            <Grid item xs={8}>
              <Grid
                container
                rowSpacing={3}
                spacing={6}>

                <Grid item xs={6}>
                  <ControlledTextField
                    label="First Name"
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="firstName" />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField
                    label="Last Name"
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="lastName" />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField
                    label="Company Name"
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="companyName" />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField
                    label="Website"
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="websiteUrl" />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField
                    label="Email"
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="email" />
                </Grid>

                <Grid item xs={6}>
                  <ControlledTextField
                    label="Phone Number"
                    isDisabled={loading}
                    formControl={form.control}
                    fieldKey="phoneNumber" />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <InvestmentCategorySelectFieldController
                label="Category"
                isDisabled={loading}
                formControl={form.control}
                fieldKey="investmentCategory" />
            </Grid>

            <Grid item xs={6}>
              <InvestmentAreaMultiSelectFieldController
                formControl={form.control}
                label="Industry"
                fieldKey="investmentAreas"
                isDisabled={loading} />
            </Grid>

            <Grid item xs={6}>
              <InvestmentStageMultiSelectFieldController
                formControl={form.control}
                label="Investment Stage"
                fieldKey="investmentStages"
                isDisabled={loading} />
            </Grid>

            <Grid item xs={6}>
              {isInvestor && (
                <InvestmentGeographyGroupedMultiSelectFieldController
                  formControl={form.control}
                  fieldKey="investmentGeographies"
                  isDisabled={loading} />
              )}
            </Grid>

            <Grid item xs={12}>
              <ControlledTextField
                formControl={form.control}
                label="Company Description"
                characterCount={characterCount}
                numLines={6}
                fieldKey="companyDescription"
                isDisabled={loading} />
            </Grid>

            {isInvestor && (
              <RowGrid item xs={6}>
                <Stack direction='row' alignItems='center' spacing={2}>
                  <RoundPadBox>
                    <MoneyIcon/>
                  </RoundPadBox>

                  <Box>
                    <ControlledTextField
                      type='number'
                      label="Low"
                      fieldKey="checkSizeMinDollars"
                      formControl={form.control}
                      isDisabled={loading} />
                  </Box>
                </Stack>

                <HorizontalBar />

                <Stack direction='row' alignItems='center' spacing={2}>
                  <RoundPadBox>
                    <MoneyIcon />
                  </RoundPadBox>

                  <Box>
                    <ControlledTextField
                      type='number'
                      label="High"
                      fieldKey="checkSizeMaxDollars"
                      formControl={form.control}
                      isDisabled={loading} />
                  </Box>
                </Stack>
              </RowGrid>
            )}

            {isInvestor && (
              <Grid item xs={6}>
                <CanLeadInvestmentsSelectFieldController
                  fieldKey='canLeadInvestments'
                  formControl={form.control}
                  label='Leads Investments (for investors only)'
                  isDisabled={loading} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={4}>
              <ControlledTextField
                label="City"
                isDisabled={loading}
                formControl={form.control}
                fieldKey="city" />
            </Grid>

            <Grid item xs={1.2}>
              <USStateSelectFieldController
                label="State"
                isDisabled={loading}
                formControl={form.control}
                fieldKey="usState" />
            </Grid>

            <Grid item xs={1.2}>
              <ControlledTextField
                label="Country"
                isDisabled={loading}
                formControl={form.control}
                fieldKey="country" />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={loading}
            variant="outlined"
            color="secondary"
            onClick={handleClose}>
            Cancel
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            disabled={loading}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
